/**
 * Re-authentication prompt for an expired session.
 *
 * A dialog rather than a redirect. The employee was part-way through a draft or reading a cited
 * answer, and a redirect to the login page throws that away for a reason that has nothing to do with
 * them. Signing in here restores the session and leaves the page as it was.
 *
 * The employee id is shown, not asked for: this is the same person resuming, and re-typing an id the
 * page already knows is friction with no security value. It is `readOnly` rather than absent so the
 * browser's password manager still matches the entry.
 */

import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "../api/client";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { PasswordInput } from "../ui/PasswordInput";
import { Modal } from "../ui/Modal";
import { useSession } from "./SessionProvider";

export function SessionExpiredDialog(): JSX.Element | null {
  const { t } = useTranslation("auth");
  const { status, identity, signIn, signOut } = useSession();
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (status !== "expired") return null;

  const employeeId = identity?.employee_id ?? "";

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signIn(employeeId, password);
      setPassword("");
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.status === 429 && typeof caught.detail === "string"
          ? caught.detail
          : t("expired.invalid"),
      );
      setPassword("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      // Not dismissable into a broken page: the only ways out are signing back in or signing out.
      // `onClose` fires on Escape, and treating that as "sign out" is the honest reading — the
      // session is already gone.
      onClose={() => void signOut()}
      title={t("expired.heading")}
      closeLabel={t("expired.close")}
      footer={
        <>
          <Button type="button" variant="ghost" onClick={() => void signOut()}>
            {t("session.signOut")}
          </Button>
          <Button type="submit" form="nab-reauth" variant="primary" disabled={busy || !password}>
            {busy ? t("expired.signingIn") : t("expired.signIn")}
          </Button>
        </>
      }
    >
      <p className="text-body-sm text-textMuted">{t("expired.explanation")}</p>
      <form id="nab-reauth" className="mt-4 flex flex-col gap-4" onSubmit={submit} noValidate>
        <Input
          label={t("login.employeeId")}
          value={employeeId}
          readOnly
          autoComplete="username"
          dir="ltr"
        />
        <PasswordInput
          label={t("login.password")}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="current-password"
          autoFocus
          required
          dir="ltr"
          {...(error ? { error } : {})}
          showLabel={t("password.show")}
          hideLabel={t("password.hide")}
        />
      </form>
    </Modal>
  );
}
